/**
 * Mode-driven registration of the WebMCP tool groups (SPEC §5 lifecycle):
 *
 * - `readers`, `dialog` and `transport` are registered once and stay;
 * - `writers` are registered only while the app is `idle`;
 * - `recording` (`stop_recording`) only while `recording`;
 * - nothing variable while `auditioning`.
 *
 * So the agent sees 15 tools idle, 8 recording, 7 auditioning. The variable
 * group is disposed in full before the next one registers (Chrome throws on
 * a duplicate `registerTool` name), and every swap goes through
 * `webmcp/register.ts`'s `ToolGroup`, whose register/dispose fire the native
 * `toolchange` event and the local registry listeners the header pill uses.
 * Mode changes are followed from both `controller.onModeChange` and the
 * store's own `mode` notifications, so a mode set directly on the store (the
 * audition tool does that) re-registers too; repeated notifications of the
 * same mode are no-ops.
 *
 * The swap runs one macrotask AFTER the mode change, never synchronously
 * inside it: `stop_recording` flips the mode to idle from inside its own
 * `execute()`, and disposing the `recording` group there would abort the
 * registration signal of the very call still in flight — Chrome then rejects
 * `executeTool` with an `UnknownError` even though the tool succeeded. The
 * deferred swap lets the result reach the caller first; `getTools()` and the
 * native `toolchange` follow within the same turn.
 */
import { ToolGroup, registry, type ToolDef } from '../webmcp/register';
import type { AppMode } from '../types';
import { createTools, type ToolDeps } from './executors';
import { TOOL_GROUPS, TOOL_NAMES, type ToolGroup as GroupName, type ToolName } from './schemas';

/** Handle returned by `mountToolGroups`. */
export interface ToolGroupsHandle {
  /** Unregister every group and release the tools. Idempotent. */
  dispose(): void;
  /** Names currently registered, in `TOOL_NAMES` order (for tests and the header pill). */
  current(): ToolName[];
  /** The app mode the registration currently reflects. */
  mode(): AppMode;
}

/** Group names registered in every mode. */
export const ALWAYS_GROUPS: readonly GroupName[] = ['readers', 'dialog', 'transport'];

/** Delay before a mode change re-registers the variable group (0 = next macrotask; see module header). */
export const SWAP_DELAY_MS = 0;

/** The variable group for a mode, or null while auditioning. */
export function variableGroupFor(mode: AppMode): GroupName | null {
  if (mode === 'idle') return 'writers';
  if (mode === 'recording') return 'recording';
  return null;
}

/**
 * Create the tools for `deps` and keep their registration in step with the
 * app mode. Returns a handle with `current()` for the header pill and tests.
 */
export function mountToolGroups(deps: ToolDeps): ToolGroupsHandle {
  const { store, controller } = deps;
  const tools = createTools(deps);
  const defsOf = (group: GroupName): ToolDef[] => TOOL_GROUPS[group].map((name) => tools.defs[name]);

  const always = new ToolGroup('bandmate-core', ALWAYS_GROUPS.flatMap(defsOf)).register();
  let variable: ToolGroup | null = null;
  let variableName: GroupName | null = null;
  let disposed = false;
  let pending: ReturnType<typeof setTimeout> | null = null;

  const sync = (mode: AppMode): void => {
    if (disposed) return;
    const want = variableGroupFor(mode);
    if (want === variableName && (variable === null || !variable.disposed)) return;
    variable?.dispose();
    variable = null;
    variableName = want;
    if (want) variable = new ToolGroup(`bandmate-${want}`, defsOf(want)).register();
  };

  /** Swap after the current call (which may be the tool that changed the mode) has returned; coalesces bursts. */
  const scheduleSync = (): void => {
    if (disposed || pending !== null) return;
    pending = setTimeout(() => {
      pending = null;
      sync(store.mode());
    }, SWAP_DELAY_MS);
  };

  sync(store.mode());
  const offController = controller.onModeChange(() => scheduleSync());
  const offStore = store.subscribe((_s, meta) => {
    if (meta.source === 'mode') scheduleSync();
  });

  return {
    dispose() {
      if (disposed) return;
      disposed = true;
      if (pending !== null) clearTimeout(pending);
      pending = null;
      offController();
      offStore();
      variable?.dispose();
      variable = null;
      always.dispose();
      tools.dispose();
    },
    current() {
      const registered = new Set(registry.list().map((t) => t.def.name));
      return TOOL_NAMES.filter((name) => registered.has(name));
    },
    mode: () => (variableName === 'writers' ? 'idle' : variableName === 'recording' ? 'recording' : 'auditioning'),
  };
}
